import React, { useState } from 'react';
import { Check, X } from 'lucide-react';

export interface BaseInteractiveProps {
  exercise: any;
  onAnswer: (answer: any) => void;
  showFeedback?: boolean;
  disabled?: boolean;
}

// Multiple Choice Component (default)
export const MultipleChoiceComponent: React.FC<BaseInteractiveProps> = ({
  exercise,
  onAnswer,
  showFeedback = false,
  disabled = false
}) => {
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);

  const handleSelect = (index: number) => {
    if (disabled) return;
    setSelectedAnswer(index);
    onAnswer(index);
  };

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <p className="text-gray-700 mb-3">{exercise.content}</p>
        <p className="font-medium text-gray-900 mb-3">{exercise.question}</p>
      </div>

      <div className="space-y-2">
        {exercise.options?.map((option: string, index: number) => (
          <label
            key={index}
            className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
              selectedAnswer === index
                ? showFeedback
                  ? exercise.correctAnswer === index
                    ? 'bg-green-50 border-green-300'
                    : 'bg-red-50 border-red-300'
                  : 'bg-blue-50 border-blue-300'
                : 'border-gray-200 hover:bg-gray-50'
            } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
          >
            <input
              type="radio"
              name={`exercise-${exercise.id || 'default'}`}
              checked={selectedAnswer === index}
              onChange={() => handleSelect(index)}
              disabled={disabled}
              className="text-blue-600"
            />
            <span className="text-gray-700 flex-1">{option}</span>
            {showFeedback && selectedAnswer === index && (
              <span
                className={`text-sm font-medium ${
                  exercise.correctAnswer === index ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {exercise.correctAnswer === index ? 'Correct' : 'Incorrect'}
              </span>
            )}
          </label>
        ))}
      </div>

      {showFeedback && selectedAnswer !== null && exercise.explanation && (
        <div
          className={`p-4 rounded-lg border ${
            exercise.correctAnswer === selectedAnswer
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          }`}
        >
          <div className="flex items-start gap-2">
            {exercise.correctAnswer === selectedAnswer ? (
              <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            ) : (
              <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            )}
            <div>
              <p
                className={`text-sm font-medium mb-1 ${
                  exercise.correctAnswer === selectedAnswer ? 'text-green-800' : 'text-red-800'
                }`}
              >
                Explanation:
              </p>
              <p
                className={`text-sm ${
                  exercise.correctAnswer === selectedAnswer ? 'text-green-700' : 'text-red-700'
                }`}
              >
                {exercise.explanation}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
